import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Switch, Typography } from '@mui/material';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { visuallyHiddenSx } from '../Fields/fieldStyles';

import {
  inlineValueSx,
  rowChildrenSx,
  rowDescriptionSx,
  rowHeaderSxOf,
  rowLabelSx,
  rowRootSx,
  rowSwitchSx,
  rowTargetHeaderSxOf,
  rowTextSx,
  rowTrailSx,
  stackedLabelSx,
  stackedValueSx,
  switchLabelSx,
} from './settingsListStyles';

type SettingsRowControl =
  | { control?: 'static'; trailing?: React.ReactNode }
  | { control: 'link'; to: string; state?: unknown }
  | { control: 'external'; href: string }
  | { control: 'button'; onClick: () => void; 'aria-expanded'?: boolean; 'aria-controls'?: string }
  | {
      control: 'switch';
      checked: boolean;
      onCheckedChange: (next: boolean) => void;
      disabled?: boolean;
      busy?: boolean;
    };

export type SettingsRowProps = SettingsRowControl & {
  label: React.ReactNode;
  description?: React.ReactNode;
  value?: React.ReactNode;
  leading?: React.ReactNode;
  status?: React.ReactNode;
  layout?: 'inline' | 'stacked';
  children?: React.ReactNode;
  component?: 'li' | 'div';
  id?: string;
};

const inlineSlotSx = { display: 'inline-flex' } as const;

const has = (node: React.ReactNode): boolean => node !== undefined && node !== null && node !== false && node !== '';

interface RowIds {
  leading: string;
  label: string;
  value: string;
  description: string;
  status: string;
  newTab: string;
  control: string;
}

const useRowIds = (): RowIds => {
  const base = useId();
  return {
    leading: `${base}-leading`,
    label: `${base}-label`,
    value: `${base}-value`,
    description: `${base}-description`,
    status: `${base}-status`,
    newTab: `${base}-new-tab`,
    control: `${base}-control`,
  };
};

interface RowParts {
  label: React.ReactNode;
  description?: React.ReactNode;
  value?: React.ReactNode;
  leading?: React.ReactNode;
  status?: React.ReactNode;
  layout: 'inline' | 'stacked';
  ids: RowIds;
}

const RowText: React.FC<RowParts & { labelFor?: string }> = ({ label, description, value, layout, ids, labelFor }) => {
  const stacked = layout === 'stacked';
  return (
    <Box component="span" sx={rowTextSx}>
      {labelFor === undefined ? (
        <Typography id={ids.label} component="span" variant={stacked ? 'label' : 'highlight'} sx={stacked ? stackedLabelSx : rowLabelSx}>
          {label}
        </Typography>
      ) : (
        <Typography id={ids.label} component="label" htmlFor={labelFor} variant="highlight" sx={switchLabelSx}>
          {label}
        </Typography>
      )}
      {stacked && has(value) && (
        <Typography id={ids.value} component="span" variant="body" sx={stackedValueSx}>
          {value}
        </Typography>
      )}
      {has(description) && (
        <Typography id={ids.description} component="span" variant="label" sx={rowDescriptionSx}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

const RowTrail: React.FC<RowParts & { end?: React.ReactNode }> = ({ value, status, layout, ids, end }) => (
  <Box component="span" sx={rowTrailSx}>
    {layout === 'inline' && has(value) && (
      <Typography id={ids.value} component="span" variant="label" sx={inlineValueSx}>
        {value}
      </Typography>
    )}
    {has(status) && (
      <Box component="span" id={ids.status} sx={inlineSlotSx}>
        {status}
      </Box>
    )}
    {end}
  </Box>
);

const hasTrailOf = (parts: RowParts, end: React.ReactNode): boolean =>
  (parts.layout === 'inline' && has(parts.value)) || has(parts.status) || has(end);

const RowContent: React.FC<RowParts & { end?: React.ReactNode; labelFor?: string }> = ({ end, labelFor, ...parts }) => (
  <>
    {has(parts.leading) && (
      <Box component="span" id={parts.ids.leading} sx={inlineSlotSx}>
        {parts.leading}
      </Box>
    )}
    <RowText {...parts} labelFor={labelFor} />
    {hasTrailOf(parts, end) && <RowTrail {...parts} end={end} />}
  </>
);

const labelledByOf = (parts: RowParts, extra: string[] = []): string =>
  [
    has(parts.leading) ? parts.ids.leading : null,
    parts.ids.label,
    has(parts.value) ? parts.ids.value : null,
    has(parts.status) ? parts.ids.status : null,
    ...extra,
  ]
    .filter((id): id is string => id !== null)
    .join(' ');

const CHEVRON = <ChevronRightIcon fontSize="small" />;

const NewTabNote: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  return (
    <>
      <OpenInNewIcon fontSize="small" />
      <Box component="span" id={id} sx={visuallyHiddenSx}>
        {t('common.opensInNewTab', '(opens in a new tab)')}
      </Box>
    </>
  );
};

const RowHeader: React.FC<{ row: SettingsRowProps; parts: RowParts }> = ({ row, parts }) => {
  const hasLeading = has(parts.leading);
  const describedBy = has(parts.description) ? parts.ids.description : undefined;

  switch (row.control) {
    case 'link':
      return (
        <Box
          component={RouterLink}
          to={row.to}
          state={row.state}
          aria-labelledby={labelledByOf(parts)}
          aria-describedby={describedBy}
          sx={rowTargetHeaderSxOf(hasLeading)}
        >
          <RowContent {...parts} end={CHEVRON} />
        </Box>
      );
    case 'external':
      return (
        <Box
          component="a"
          href={row.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-labelledby={labelledByOf(parts, [parts.ids.newTab])}
          aria-describedby={describedBy}
          sx={rowTargetHeaderSxOf(hasLeading)}
        >
          <RowContent {...parts} end={<NewTabNote id={parts.ids.newTab} />} />
        </Box>
      );
    case 'button':
      return (
        <Box
          component="button"
          type="button"
          onClick={row.onClick}
          aria-expanded={row['aria-expanded']}
          aria-controls={row['aria-controls']}
          aria-labelledby={labelledByOf(parts)}
          aria-describedby={describedBy}
          sx={rowTargetHeaderSxOf(hasLeading)}
        >
          <RowContent {...parts} end={CHEVRON} />
        </Box>
      );
    case 'switch': {
      const toggle = (
        <Switch
          id={parts.ids.control}
          size="medium"
          checked={row.checked}
          disabled={row.disabled}
          onChange={(_event, next) => row.onCheckedChange(next)}
          inputProps={{ role: 'switch', 'aria-describedby': describedBy, 'aria-busy': row.busy === true ? true : undefined }}
          sx={rowSwitchSx}
        />
      );
      return (
        <Box sx={rowHeaderSxOf(hasLeading, true)}>
          <RowContent {...parts} end={toggle} labelFor={parts.ids.control} />
        </Box>
      );
    }
    default: {
      const end = row.trailing;
      return (
        <Box sx={rowHeaderSxOf(hasLeading, hasTrailOf(parts, end))}>
          <RowContent {...parts} end={end} />
        </Box>
      );
    }
  }
};

export const SettingsRow: React.FC<SettingsRowProps> = (row) => {
  const ids = useRowIds();
  const { label, description, value, leading, status, layout = 'inline', children, component = 'li', id } = row;
  const parts: RowParts = { label, description, value, leading, status, layout, ids };
  return (
    <Box component={component} id={id} sx={rowRootSx}>
      <RowHeader row={row} parts={parts} />
      {has(children) && <Box sx={rowChildrenSx}>{children}</Box>}
    </Box>
  );
};
